import type { ReactNode } from "react";

import { useDataset } from "@/shared/domain/datasets/hooks";
import {
  ConfirmationAlert,
  ConfirmationCard,
  ConfirmationField,
  ConfirmationFields,
  ConfirmationPanel,
} from "@/shared/foundation/primitives";

import type { ConfirmationContentProps } from "./ClientTools";
import type { NavigableRouteParam } from "./navigableRoutes";
import { findNavigableRoute, isParameterized } from "./navigableRoutes";

const PREVIEW_LABEL = "Navigate preview";

/**
 * A non-empty string param of the proposal, or `undefined` for anything else.
 *
 * `client_navigate` declares no `validateInput`, so nothing has checked what the
 * model sent. An empty string reads as absent, the same verdict `execute`
 * reaches with its own falsy check.
 */
function proposedString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/** The `params` object of the proposal; `{}` for anything that isn't one. */
function proposedParams(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * The dataset's name, falling back to its ID until the record lands — and if it
 * never does, so Approve is live and correctly placed from the first paint.
 *
 * Datasets are the only ID resolved here: they are the most common navigation
 * target, and a bare `ds_…` is what made these cards read as debug output. Its
 * own component so the lookup runs only for a proposal that names one.
 */
function DatasetName({ datasetId }: { datasetId: string }) {
  const dataset = useDataset(datasetId);
  return <>{dataset.data?.name ?? datasetId}</>;
}

function ParamValue({
  param,
  value,
}: {
  param: NavigableRouteParam;
  value: string;
}) {
  if (param.name === "datasetId") {
    return <DatasetName datasetId={value} />;
  }
  return <>{value}</>;
}

/** Replaces the card when the navigation cannot succeed, with the one reason. */
function Refusal({ children }: { children: ReactNode }) {
  return (
    <ConfirmationPanel label={PREVIEW_LABEL}>
      <ConfirmationAlert>
        This request won&rsquo;t succeed. {children}
      </ConfirmationAlert>
    </ConfirmationPanel>
  );
}

/**
 * Read-only preview of a `client_navigate` call, shown while the user decides
 * whether to approve it.
 *
 * Navigation changes nothing but what is on screen, so the card answers one
 * question — where is this about to take me — rather than itemizing arguments.
 *
 * A proposal `execute` would refuse says so instead of offering Approve over a
 * call that can only come back as an error: the model picks its route name from
 * a fixed list, but nothing stops it inventing one or omitting the ID the route
 * needs.
 */
export function NavigateConfirmation({ params }: ConfirmationContentProps) {
  const routeName = proposedString(params.route);
  const route =
    routeName === undefined ? undefined : findNavigableRoute(routeName);

  if (route === undefined) {
    return (
      <Refusal>
        {routeName === undefined
          ? "No page was named."
          : `There is no "${routeName}" page.`}
      </Refusal>
    );
  }

  const proposed = proposedParams(params.params);
  const declared = isParameterized(route) ? route.params : [];

  // No route declares more than one required param, so the first one missing is
  // the whole reason `execute` would throw.
  const missing = declared.find(
    (param) =>
      param.required === true &&
      proposedString(proposed[param.name]) === undefined,
  );
  if (missing !== undefined) {
    return <Refusal>No {missing.label.toLowerCase()} was named.</Refusal>;
  }

  // Params the route never declared are dropped: `execute` reads only the ones
  // named here, so showing them would overstate the request.
  const provided = declared.flatMap((param) => {
    const value = proposedString(proposed[param.name]);
    return value === undefined ? [] : [{ param, value }];
  });

  return (
    <ConfirmationPanel label={PREVIEW_LABEL}>
      <ConfirmationCard title={`Go to ${route.label}`}>
        {provided.length > 0 && (
          <ConfirmationFields>
            {provided.map(({ param, value }) => (
              <ConfirmationField key={param.name} label={param.label}>
                <ParamValue param={param} value={value} />
              </ConfirmationField>
            ))}
          </ConfirmationFields>
        )}
      </ConfirmationCard>
    </ConfirmationPanel>
  );
}
